import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  Modal,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import {
  portalApi,
  PortalWebhookSubscription,
  PortalWebhookSubscriptionCreated
} from './portalClient';

const SUPPORTED_EVENTS = [
  'order.created', 'order.item_added', 'order.item_removed', 'order.voided',
  'payment.authorized', 'payment.captured', 'payment.voided', 'payment.refunded',
  'table.seated', 'table.check_opened', 'table.check_closed',
  'waitlist.party_added', 'waitlist.party_seated',
  'inventory.count_completed', 'inventory.item_below_par',
  'review.received', 'review.response_posted',
  'employee.clocked_in', 'employee.clocked_out',
  'equipment.alert_triggered'
];

const Inner: React.FC = () => {
  const { isAuthenticated, getIdToken } = useAuth();
  const { show } = useToast();
  const [loading, setLoading] = React.useState(false);
  const [subs, setSubs] = React.useState<PortalWebhookSubscription[]>([]);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newUrl, setNewUrl] = React.useState('');
  const [newEvents, setNewEvents] = React.useState<string[]>([]);
  const [creating, setCreating] = React.useState(false);
  const [createdSub, setCreatedSub] = React.useState<PortalWebhookSubscriptionCreated | null>(null);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await portalApi.listWebhookSubscriptions(getIdToken);
      setSubs(data);
    } catch (err: any) {
      show({ tone: 'error', title: 'Failed to load subscriptions', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getIdToken, show]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async () => {
    if (!/^https:\/\//i.test(newUrl)) {
      show({ tone: 'error', title: 'HTTPS URL required' });
      return;
    }
    if (newEvents.length === 0) {
      show({ tone: 'error', title: 'Pick at least one event' });
      return;
    }
    setCreating(true);
    try {
      const created = await portalApi.createWebhookSubscription(getIdToken, {
        url: newUrl.trim(),
        events: newEvents
      });
      setCreatedSub(created);
      setCreateOpen(false);
      setNewUrl('');
      setNewEvents([]);
      await refresh();
    } catch (err: any) {
      show({ tone: 'error', title: 'Could not create subscription', description: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (sub: PortalWebhookSubscription) => {
    if (!window.confirm(`Delete subscription for ${sub.url}?`)) return;
    try {
      await portalApi.deleteWebhookSubscription(getIdToken, sub.id);
      show({ tone: 'success', title: 'Subscription deleted' });
      await refresh();
    } catch (err: any) {
      show({ tone: 'error', title: 'Delete failed', description: err.message });
    }
  };

  const columns: DataTableColumn<PortalWebhookSubscription>[] = [
    { key: 'url', header: 'URL', accessor: (r) => r.url, sortable: true },
    { key: 'events', header: 'Events', accessor: (r) => `${r.events.length}` },
    { key: 'createdAt', header: 'Created', accessor: (r) => new Date(r.createdAt).toLocaleString(), sortable: true },
    {
      key: 'actions',
      header: '',
      accessor: (r) => (
        <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}>
          Delete
        </Button>
      ),
      align: 'right'
    }
  ];

  if (!isAuthenticated) {
    return (
      <>
        <h1 className="dev-portal__title">Webhooks</h1>
        <p className="dev-portal__lede">
          Sign in to manage webhook subscriptions for your enterprise.
        </p>
        <Card>
          <Card.Body>
            <Card.Title>Not signed in</Card.Title>
            <p className="dev-portal__muted" style={{ margin: '8px 0 0' }}>
              <a href="/login">Sign in</a> to create or remove subscriptions.
            </p>
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <>
      <h1 className="dev-portal__title">Webhooks</h1>
      <p className="dev-portal__lede">
        BSO delivers domain events as HTTPS POSTs signed with HMAC-SHA256. See
        the{' '}
        <a href="/developers/docs" target="_blank" rel="noreferrer">
          API reference
        </a>{' '}
        for the verification recipe.
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button onClick={() => setCreateOpen(true)} disabled={loading}>
          Add subscription
        </Button>
      </div>

      <DataTable<PortalWebhookSubscription>
        columns={columns}
        rows={subs}
        getRowKey={(r) => r.id}
        pageSize={10}
        emptyState={loading ? 'Loading…' : 'No subscriptions yet.'}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add webhook subscription"
        description="We'll POST selected events to your URL with X-BSO-Signature."
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={creating}>
              Subscribe
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextInput
            label="Endpoint URL"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://hooks.mycompany.com/bso"
            required
          />
          <fieldset style={{ border: '1px solid var(--bso-border-subtle)', borderRadius: 8, padding: 12 }}>
            <legend style={{ padding: '0 8px', fontSize: 12, color: 'var(--bso-text-secondary)' }}>
              Events
            </legend>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
              {SUPPORTED_EVENTS.map((evt) => (
                <label key={evt} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={newEvents.includes(evt)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewEvents([...newEvents, evt]);
                      } else {
                        setNewEvents(newEvents.filter((x) => x !== evt));
                      }
                    }}
                  />
                  <code>{evt}</code>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      </Modal>

      <Modal
        open={createdSub !== null}
        onClose={() => setCreatedSub(null)}
        title="Signing secret (save now)"
        description="This is the only time the signing secret will be shown."
        size="md"
        footer={<Button onClick={() => setCreatedSub(null)}>Done</Button>}
      >
        {createdSub && (
          <>
            <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>
              Subscription <code>{createdSub.id}</code> → {createdSub.url}
            </p>
            <pre className="dev-portal__code" style={{ marginTop: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {createdSub.signingSecret}
            </pre>
          </>
        )}
      </Modal>
    </>
  );
};

export const DevWebhooks: React.FC = () => (
  <ToastProvider>
    <Inner />
  </ToastProvider>
);
